import { i18n } from '@/i18n';
// eslint-disable-next-line import/no-cycle
import { store } from '@/store';
// eslint-disable-next-line import/no-cycle
import {
  ActiveLiveEditorItemData,
  ElementPartTypeEnum,
  HistoryType,
  TemplateStructureEnum,
} from '@/types';
import { repositionSpecificElement, initIconPositioning } from './icon-positioning';
import { addDOMLabelToNewElement, initDOMLabels } from './dom-labels';

// eslint-disable-next-line import/no-cycle
import {
  refreshSectionsConfig,
  removeActiveStyles,
  removeHoverStyles,
  removeSortableStyles,
  removeUnusedTranslations,
  resetActiveSection,
  setActiveSection,
  updateSectionsInState,
  removeCustomWidgetCss,
  TemplateEditorState as state,
  togglePanel,
  showColumnWidgetModal,
  markTemplateAsEdited,
  removeFormConfig,
  removeTemplateFromFunnels,
  removeEditorButtons,
  updateTemplateConfigurationNextIdTemplate,
  syncGameFunnelSteps,
  cleanupGameFunnelsAfterWidgetRemoval,
  updateDisclosureAfterWidgetRemoval,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { executeWhenDragComplete, handleWidgetColumnItemDecorations, resetListeners } from '@/components/template-builder/utils/listeners';
// eslint-disable-next-line import/no-cycle
import { checkIfSavePointNeeded, createHistory } from '@/composables/template-editor/History';
import { computed, ComputedRef } from 'vue';
// eslint-disable-next-line import/no-cycle
import { showAIModal } from '@/composables/services/ShopimindAI';
// eslint-disable-next-line import/no-cycle
import {
  ACTIONS_BUTTON_GROUP, AI_BUTTON_IDENTIFIER,
  BUILDER_ELEMENT_ACTIVE_CLASS,
  BUILDER_ELEMENT_HOVER_CLASS,
  BUILDER_SORTABLE_CHOSEN_CLASS,
  BUILDER_SORTABLE_DRAG_CLASS,
  BUILDER_SORTABLE_GHOST_CLASS,
  DUPLICATE_BUTTON_IDENTIFIER,
  EDIT_COLUMN_BUTTON_IDENTIFIER,
  EDIT_SYNC_ELEMENT_BUTTON_IDENTIFIER,
  REMOVE_BUTTON_IDENTIFIER,
  SAVE_BUTTON_IDENTIFIER,
  TEMPLATE_PART_ELEMENT_PART_TYPE,
  TEMPLATE_SECTION_CLASS,
  TEMPLATE_SECTION_IDENTIFIER,
  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
  TEMPLATE_SYNC_ELEMENT_NAME,
  WIDGET_DRAGGABLE_CLASS,
  WIDGET_DRAGGABLE_IDENTIFIER,
} from './constants';
// eslint-disable-next-line import/no-cycle
import {
  createCustomButton,
  decorateActiveItemElementWithStyles,
  decorateElementWithStyles,
  getElementFromIframe,
  getSectionParent,
  getTemplateIframeDocument,
  getWidgetType,
  isDisplayTemplate,
  isEmailTemplate,
} from './helpers';
import {
  faAiAltRegular, faCloneRegular, faColumn, faEditPenSqaure, faSaveRegular, faTrashAltRegular,
} from './icons-definition';
// eslint-disable-next-line import/no-cycle
import {
  actionsButtonGroupStyles,
  AIButtonStyles,
  columnActionsButtonGroupStyles,
  secondaryButtonStyles,
  sectionActionsButtonGroupStyles,
} from './elements-style-definitions';
// eslint-disable-next-line import/no-cycle
import {
  onDuplicateLine, onDuplicateSection, onDuplicateWidget, onRemoveElement,
  removeColumn,
} from './action-buttons-handlers';
// eslint-disable-next-line import/no-cycle
import { cleanupDynamicStyle } from './parser';
import { removeWidgetCssIfUnused } from './widget-css-manager';

let translation: any;
(async () => {
  translation = await i18n;
})();

const createRemoveButton = (activeItemData: ActiveLiveEditorItemData, verifySection = false, onClickHandler: ((event: Event) => void) | null = null) => {
  const customButtonData = {
    id: `${activeItemData.type}-${REMOVE_BUTTON_IDENTIFIER}`,
    title: translation.global.t('templateLiveView.actionsButtonGroup.remove'),
    children: faTrashAltRegular,
    styles: secondaryButtonStyles,
    onClick: (e: Event) => {
      e.stopPropagation();
      const template = getTemplateIframeDocument();
      const element = template?.querySelector(activeItemData.selector);

      if (element) {
        const acceptCallback = () => {
          // Fire-and-forget: start save point check now (captures pre-change state synchronously)
          const savePointPromise = checkIfSavePointNeeded();

          // IMMEDIATE visual operations (no async wait)
          const section = (element.matches(TEMPLATE_SECTION_IDENTIFIER)) ? element : element?.closest('[data-spmelementid]');

          if (section) {
            const sectionId = section.getAttribute('data-spmelementid');

            if (sectionId) {
              // Mark section as active
              setActiveSection(parseInt(sectionId, 10));
            }
          }

          // Capture widget types before DOM removal for CSS cleanup
          const widgetTypesToCleanup: string[] = [];
          if (activeItemData.type === TemplateStructureEnum.WIDGET) {
            const wt = element.getAttribute('data-widgettype');
            if (wt) widgetTypesToCleanup.push(wt);
          } else {
            // For sections/columns, collect all contained widget types
            element.querySelectorAll('[data-widgettype]').forEach((w) => {
              const wt = w.getAttribute('data-widgettype');
              if (wt && !widgetTypesToCleanup.includes(wt)) widgetTypesToCleanup.push(wt);
            });
          }
          const isRemovedGameWidget = activeItemData.type === TemplateStructureEnum.WIDGET
            && ['spm_widget_game_wheel', 'spm_widget_game_slot_machine', 'spm_widget_game_scratch_card']
              .includes(element.getAttribute('data-widgettype') || '');

          removeTemplateFromFunnels(element as HTMLElement);
          onRemoveElement(activeItemData.selector);
          if (isRemovedGameWidget) {
            cleanupGameFunnelsAfterWidgetRemoval();
          }
          cleanupDynamicStyle();

          let historyType: HistoryType | null;
          switch (activeItemData.type) {
            case TemplateStructureEnum.WIDGET:
              historyType = HistoryType.DELETE_WIDGET;
              break;
            case TemplateStructureEnum.COLUMN:
              historyType = HistoryType.DELETE_COLUMN;
              break;
            case TemplateStructureEnum.SECTION:
              historyType = HistoryType.DELETE_SECTION;
              break;
            default:
              historyType = null;
              break;
          }

          const selectedSyncElement = store.getters['liveEditor/getSelectedSyncElement'];

          // Reset listeners immediately so remaining elements are decorated and interactive
          resetListeners();
          resetActiveSection();
          store.commit('liveEditor/resetSelectedStructure');
          if (selectedSyncElement) {
            togglePanel('configurationPanel', null).then(() => {
              if (selectedSyncElement) {
                store.dispatch('liveEditor/prepareStructureConfiguration', selectedSyncElement.structureConfiguration);
              }
            });
          }

          // Defer heavy work to after browser repaint, chained on savePointPromise for history ordering
          setTimeout(() => {
            savePointPromise.then(() => {
              executeWhenDragComplete(() => {
                updateSectionsInState(false);

                if (historyType && !selectedSyncElement) {
                  createHistory(historyType);
                }
                removeUnusedTranslations();
                removeCustomWidgetCss(activeItemData.selector);

                // Cleanup widget static CSS if no more instances of these widget types
                widgetTypesToCleanup.forEach((wt) => removeWidgetCssIfUnused(wt));

                refreshSectionsConfig();

                syncGameFunnelSteps();
                updateTemplateConfigurationNextIdTemplate();
                removeFormConfig(activeItemData.selector);
                updateDisclosureAfterWidgetRemoval();
              });
            });
          }, 0);
        };
        if (verifySection) {
        // SYNC ELEMENT WARNING
          const sectionParent = getSectionParent(element as HTMLElement);
          if (sectionParent) {
            const elementSyncId = sectionParent.getAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER);
            const selectedSyncElement = store.getters['liveEditor/getSelectedSyncElement'];
            if (elementSyncId && !selectedSyncElement && isEmailTemplate(state.template?.type) && !sectionParent.getAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)) {
              store.dispatch('liveEditor/showSyncElementWarning', {
                elementId: sectionParent.getAttribute('id'),
                elementType: TemplateStructureEnum.SECTION,
                acceptCallback,
                rejectCallback: null,
              });
            } else {
              acceptCallback();
              markTemplateAsEdited();
            }
          }
        } else {
          acceptCallback();
          markTemplateAsEdited();
        }
      }
    },
  };
  if (onClickHandler) {
    customButtonData.onClick = onClickHandler;
  }
  return createCustomButton(customButtonData);
};

const createDuplicateButton = (prefix: string, onClickHandler: (event: Event) => void) => createCustomButton({
  id: `${prefix}-${DUPLICATE_BUTTON_IDENTIFIER}`,
  title: translation.global.t('templateLiveView.actionsButtonGroup.duplicate'),
  children: faCloneRegular,
  styles: secondaryButtonStyles,
  onClick: onClickHandler,
});

const createEditColumnButton = (prefix: string, onClickHandler: (event: Event) => void) => createCustomButton({
  id: `${prefix}-${EDIT_COLUMN_BUTTON_IDENTIFIER}`,
  title: translation.global.t('templateLiveView.actionsButtonGroup.editColumnSize'),
  children: faColumn,
  styles: secondaryButtonStyles,
  onClick: onClickHandler,
});

const createAIButton = (activeItemData: ActiveLiveEditorItemData) => {
  const template = getTemplateIframeDocument();
  const element = template?.querySelector(activeItemData.selector);
  let AIContentType = 0;
  const AIApplyContentParams = {
    selector: '',
    type: '',
    target: '',
    attr: '',
    imageWidth: 0,
    imageHeight: 0,
    idTemplate: 0,
  };

  if (element) {
    const classOfElement = Array.from(element.classList).filter((className) => className !== WIDGET_DRAGGABLE_CLASS.replace('.', '') && className !== 'spm_widget');
    const widgetType = getWidgetType(classOfElement[0]);

    if (state.template) {
      AIApplyContentParams.idTemplate = state.template.id;
    }

    switch (widgetType) {
      case 'widget-text':
        AIContentType = 1;
        AIApplyContentParams.selector = `${activeItemData.selector} .spm_rich_editor`;
        AIApplyContentParams.type = 'html';
        AIApplyContentParams.target = 'template';
        break;
      case 'widget-image':
        AIContentType = 3;
        AIApplyContentParams.selector = `${activeItemData.selector} img`;
        AIApplyContentParams.type = 'attr';
        AIApplyContentParams.attr = 'src';
        AIApplyContentParams.target = 'template';
        break;
      default: return false;
    }
  }
  if (AIContentType) {
    return createCustomButton({
      id: `${activeItemData.type}-${AI_BUTTON_IDENTIFIER}`,
      title: translation.global.t('templateLiveView.actionsButtonGroup.ai'),
      children: faAiAltRegular,
      styles: AIButtonStyles,
      onClick: (e: Event) => {
        e.stopPropagation();
        const activeElement = template?.querySelector(activeItemData.selector);
        if (activeElement) {
          const sectionParent = getSectionParent(activeElement as HTMLElement);
          if (sectionParent) {
            const elementSyncId = sectionParent.getAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER);
            const selectedSyncElement = store.getters['liveEditor/getSelectedSyncElement'];
            if (elementSyncId && !selectedSyncElement && isEmailTemplate(state.template?.type)) {
              store.dispatch('liveEditor/showSyncElementWarning', {
                elementId: sectionParent.getAttribute('id'),
                elementType: TemplateStructureEnum.SECTION,
                acceptCallback: () => { showAIModal(e, AIContentType, AIApplyContentParams); },
                rejectCallback: null,
              });
            } else {
              showAIModal(e, AIContentType, AIApplyContentParams);
            }
          }
        }
      },
    });
  }
  return false;
};

const getActionsButtonListByItemType = (activeItemData: ActiveLiveEditorItemData) => {
  const { type: itemType } = activeItemData;
  const actionsButtonList: Array<HTMLDivElement> = [];

  const saveSectionButton = createCustomButton({
    id: `${itemType}-${SAVE_BUTTON_IDENTIFIER}`,
    title: translation.global.t('templateLiveView.actionsButtonGroup.save'),
    children: faSaveRegular,
    styles: secondaryButtonStyles,
    onClick: (e: Event) => {
      e.stopPropagation();
      store.commit('liveEditor/showSaveElementModal', {
        selector: activeItemData.selector,
        type: itemType,
      });
    },
  });

  const saveElementButton = createCustomButton({
    id: `${itemType}-${SAVE_BUTTON_IDENTIFIER}`,
    title: translation.global.t('templateLiveView.actionsButtonGroup.save'),
    children: faSaveRegular,
    styles: secondaryButtonStyles,
    onClick: (event: Event) => {
      event.stopPropagation();
      const template = getTemplateIframeDocument();
      const element = template?.querySelector(activeItemData.selector);
      if (element) {
        const sectionParent = getSectionParent(element as HTMLElement);
        if (sectionParent && isEmailTemplate(state.template?.type)) {
          const syncParentSyncId = sectionParent.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER)
            && !sectionParent.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)
            ? sectionParent.getAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER) : '';
          const showSaveElementModalData: Record<string, any> = {
            selector: activeItemData.selector,
            type: itemType,
          };
          if (syncParentSyncId && isEmailTemplate(state.template?.type)) {
            showSaveElementModalData.syncParentSyncId = syncParentSyncId;
            showSaveElementModalData.syncParentId = sectionParent.getAttribute('id');
          }
          store.commit('liveEditor/showSaveElementModal', showSaveElementModalData);
        } else {
          store.commit('liveEditor/showSaveElementModal', {
            selector: activeItemData.selector,
            type: itemType,
          });
        }
      }
    },
  });

  let editSyncElementButtonTitle = '';
  const syncElement = getElementFromIframe(activeItemData.selector);
  if (syncElement && syncElement.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER)) {
    editSyncElementButtonTitle = translation.global.t('templateLiveView.actionsButtonGroup.editSyncWidget');
    if (syncElement.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)) {
      editSyncElementButtonTitle = translation.global.t('templateLiveView.actionsButtonGroup.editStyleSync');
    }
  }

  const editSyncElementButton = createCustomButton({
    id: `${itemType}-${EDIT_SYNC_ELEMENT_BUTTON_IDENTIFIER}`,
    title: editSyncElementButtonTitle,
    children: faEditPenSqaure,
    styles: secondaryButtonStyles,
    onClick: (event: Event) => {
      event.stopPropagation();
      const element = getElementFromIframe(activeItemData.selector);

      if (element) {
        store.dispatch('liveEditor/showSyncElementWarning', {
          elementId: element.id,
          elementType: TemplateStructureEnum.WIDGET,
          name: element.getAttribute(TEMPLATE_SYNC_ELEMENT_NAME) || '',
          elementPartType: element.getAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE) || '',
          acceptCallback: () => {
            togglePanel('configurationPanel', event)
              .then(() => {
                const selectedStructure = store.getters['liveEditor/getSelectedStructure'];
                handleWidgetColumnItemDecorations(element, TemplateStructureEnum.WIDGET, selectedStructure, element.id);
                if (element && element.classList && element.classList.contains(WIDGET_DRAGGABLE_IDENTIFIER)) {
                  store.dispatch('liveEditor/prepareStructureConfiguration', {
                    type: TemplateStructureEnum.WIDGET,
                    identifier: element.id,
                    widgetType: getWidgetType(element.getAttribute('data-widgettype')),
                  });
                }
              });
          },
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          rejectCallback: () => {},
        });
      }
    },
  });

  const template = getTemplateIframeDocument();

  // eslint-disable-next-line default-case
  switch (itemType) {
    case TemplateStructureEnum.WIDGET:
      if (!store.getters['liveEditor/getSelectedSyncElement']) {
        const widget = template.querySelector(activeItemData.selector);
        if (widget
            && !widget.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER)
            && (isEmailTemplate(state.template?.type) || isDisplayTemplate(state.template?.type))
        ) {
          actionsButtonList.push(saveElementButton);
        }
        if (widget && widget.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER)) {
          const parentSection = widget.closest('[data-spmelementid]');
          if (parentSection && parentSection.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER) && !parentSection.hasAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)) {
            actionsButtonList.push(editSyncElementButton);
          }
        }
      }
      actionsButtonList.push(createDuplicateButton(itemType, (e: Event) => {
        e.stopPropagation();
        // Check section if synchronized
        const element = template?.querySelector(activeItemData.selector);
        if (element) {
          const sectionParent = getSectionParent(element as HTMLElement);
          if (sectionParent) {
            const elementSyncId = sectionParent.getAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER);
            const selectedSyncElement = store.getters['liveEditor/getSelectedSyncElement'];
            if (elementSyncId && !selectedSyncElement && isEmailTemplate(state.template?.type) && !sectionParent.getAttribute(TEMPLATE_PART_ELEMENT_PART_TYPE)) {
              store.dispatch('liveEditor/showSyncElementWarning', {
                elementId: sectionParent.getAttribute('id'),
                elementType: TemplateStructureEnum.SECTION,
                acceptCallback: () => { onDuplicateWidget(activeItemData); },
                rejectCallback: null,
              });
            } else {
              onDuplicateWidget(activeItemData);
              markTemplateAsEdited();
            }
          }
        }
      }));
      actionsButtonList.push(createRemoveButton(activeItemData, true));
      // eslint-disable-next-line no-case-declarations
      const AIButton = createAIButton(activeItemData);
      if (AIButton) {
        actionsButtonList.push(AIButton);
      }
      if (isDisplayTemplate(state.template?.type)) {
        // Get column parent of widget
        const column = template.querySelector(activeItemData.selector)?.closest('.spm_column');
        if (column) {
          actionsButtonList.push(
            createEditColumnButton(itemType, (e: Event) => {
              e.stopPropagation();
              store.commit('liveEditor/setSelectedColumnId', `#${column.id}`);
              showColumnWidgetModal();
            }),
          );
        }
      }
      break;
    case TemplateStructureEnum.COLUMN:
      actionsButtonList.push(
        createEditColumnButton(itemType, (e: Event) => {
          e.stopPropagation();
          store.commit('liveEditor/setSelectedColumnId', activeItemData.selector);
          showColumnWidgetModal();
        }),
        createRemoveButton(activeItemData, false, (e: Event) => {
          e.stopPropagation();
          const columnToRemove = template.querySelector(activeItemData.selector);
          if (columnToRemove) {
            removeColumn(e, columnToRemove as HTMLElement);
            markTemplateAsEdited();
          }
        }),
      );
      break;
    case TemplateStructureEnum.LINE:
      actionsButtonList.push(
        createDuplicateButton(itemType, (e: Event) => {
          e.stopPropagation();
          onDuplicateLine(activeItemData);
        }),
        createRemoveButton(activeItemData),
      );
      break;
    case TemplateStructureEnum.SECTION:
      // Count sections of template. If there is only one section, we do not add the remove button
      if (template.querySelectorAll(`.${TEMPLATE_SECTION_CLASS}`).length === 1) {
        const section = template.querySelector(activeItemData.selector);
        if (section && !section.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER)) {
          actionsButtonList.push(saveSectionButton);
        }
        actionsButtonList.push(
          createDuplicateButton(itemType, (e: Event) => {
            e.stopPropagation();
            onDuplicateSection(activeItemData);
            markTemplateAsEdited();
          }),
        );
      } else {
        const section = template.querySelector(activeItemData.selector);
        if (section && !section.hasAttribute(TEMPLATE_SYNC_ELEMENT_IDENTIFIER)) {
          actionsButtonList.push(saveSectionButton);
        }
        actionsButtonList.push(
          createDuplicateButton(itemType, (e: Event) => {
            e.stopPropagation();
            onDuplicateSection(activeItemData);
            markTemplateAsEdited();
          }),
          createRemoveButton(activeItemData),
        );
      }
      break;
  }

  return actionsButtonList;
};

const getActionsButtonGroupStylesByItemType = (itemType: TemplateStructureEnum) => {
  switch (itemType) {
    case TemplateStructureEnum.COLUMN:
      return columnActionsButtonGroupStyles;
    case TemplateStructureEnum.SECTION:
      return sectionActionsButtonGroupStyles;
    default:
      return actionsButtonGroupStyles;
  }
};

export const createActionsButtonGroup = (activeItemData: ActiveLiveEditorItemData) => {
  const { type }: { type: TemplateStructureEnum } = activeItemData;
  const actionsButtonGroup = document.createElement('div');
  const actionButtonList = getActionsButtonListByItemType(activeItemData);
  const styles = getActionsButtonGroupStylesByItemType(type);

  actionsButtonGroup.id = `${type}-${ACTIONS_BUTTON_GROUP}`;
  actionsButtonGroup.className = ACTIONS_BUTTON_GROUP;

  decorateElementWithStyles(actionsButtonGroup, styles);
  
  // Hide initially to avoid flicker during repositioning
  actionsButtonGroup.style.opacity = '0';

  actionButtonList.forEach((actionButton) => {
    actionsButtonGroup.appendChild(actionButton);
  });

  // Reposition and show after element is in DOM
  setTimeout(() => {
    repositionSpecificElement(actionsButtonGroup);
  }, 50);

  return actionsButtonGroup;
};

export const removeActiveItemElementDecorations = () => {
  removeHoverStyles();
  removeActiveStyles();
  removeSortableStyles();
  removeEditorButtons();
  store.commit('liveEditor/resetActiveItemData');
};

export const setActiveItemData = (activeItemData: ActiveLiveEditorItemData) => {
  const currentActiveItemData: ComputedRef<ActiveLiveEditorItemData | null> = computed(() => store.getters['liveEditor/getActiveItemData']);

  if (!currentActiveItemData.value || currentActiveItemData.value.selector !== activeItemData.selector) {
    const selectedSyncElement = store.getters['liveEditor/getSelectedSyncElement'];
    if (activeItemData.type === TemplateStructureEnum.WIDGET) {
      const template = getTemplateIframeDocument();
      const widget = template.querySelector(activeItemData.selector);
      if (widget) {
        const section = getSectionParent(widget as HTMLElement);
        if (!selectedSyncElement || (section && selectedSyncElement.elementId !== section.getAttribute('id'))) {
          removeActiveItemElementDecorations();
        }
      }
    } else {
      removeActiveItemElementDecorations();
    }
    store.commit('liveEditor/setActiveItemData', activeItemData);
  }
};

export const addActiveItemElementDecorations = (activeItemData: ActiveLiveEditorItemData) => {
  setActiveItemData(activeItemData);

  // We create actions toolbar if the element doesn't contain one
  const template = getTemplateIframeDocument();
  let activeItemElement: HTMLElement | null = template.querySelector(activeItemData.selector);

  if (activeItemElement && !activeItemElement.classList.contains(BUILDER_ELEMENT_ACTIVE_CLASS)) {
    activeItemElement = decorateActiveItemElementWithStyles(activeItemData.selector, 'add');
  }

  const selectedSyncElement = store.getters['liveEditor/getSelectedSyncElement'];

  if (!selectedSyncElement
      && !state.leftToolbar.show.translationPanel
      && activeItemElement
      && !activeItemElement.querySelector(`.${ACTIONS_BUTTON_GROUP}`)
    && (activeItemData.type !== TemplateStructureEnum.COLUMN || (activeItemData.type === TemplateStructureEnum.COLUMN && (isDisplayTemplate(state.template?.type) || activeItemElement?.classList.contains('spm_column_with_placeholder'))))
  ) {
    const actionButtonGroup = createActionsButtonGroup(activeItemData);
    activeItemElement.appendChild(actionButtonGroup);
    actionButtonGroup.style.display = 'block';
    
    // Ajouter un label DOM pour ce nouvel élément
    addDOMLabelToNewElement(activeItemElement);
  }
};

export const addItemElementDecorations = (element: HTMLElement, type: TemplateStructureEnum) => {
  if (!element.classList.contains(BUILDER_ELEMENT_ACTIVE_CLASS)) {
    element.classList.add(BUILDER_ELEMENT_ACTIVE_CLASS);
  }

  const selectedSyncElement = store.getters['liveEditor/getSelectedSyncElement'];

  if (!state.leftToolbar.show.translationPanel
      && (!selectedSyncElement || (selectedSyncElement && selectedSyncElement.elementId !== element.id && selectedSyncElement.elementPartType !== ElementPartTypeEnum.STYLES))
      && !element.querySelector(`:scope > .${ACTIONS_BUTTON_GROUP}`)
      && (type !== TemplateStructureEnum.COLUMN || (type === TemplateStructureEnum.COLUMN && (isDisplayTemplate(state.template?.type) || element?.classList.contains('spm_column_with_placeholder'))))) {
    const actionsButtonGroup = createActionsButtonGroup({ selector: `#${element.id}`, type });
    element.appendChild(actionsButtonGroup);
    actionsButtonGroup.style.display = 'block';
    
    // Ajouter un label DOM pour ce nouvel élément
    addDOMLabelToNewElement(element);
  }
};

export const removeItemElementDecorations = (element?: HTMLElement) => {
  let elements: HTMLElement[] = [];

  if (element) {
    elements = [element];
  } else {
    const iframeDoc = typeof getTemplateIframeDocument === 'function' ? getTemplateIframeDocument() : null;
    if (iframeDoc) {
      elements = Array.from(iframeDoc.querySelectorAll(
        // eslint-disable-next-line max-len
        `.${BUILDER_ELEMENT_ACTIVE_CLASS}, .${BUILDER_ELEMENT_HOVER_CLASS}, .${BUILDER_SORTABLE_DRAG_CLASS}, .${BUILDER_SORTABLE_GHOST_CLASS}, .${BUILDER_SORTABLE_CHOSEN_CLASS}, .sortable-chosen`,
      ));
    }
  }
  elements.forEach((el) => {
    el.classList.remove(BUILDER_ELEMENT_ACTIVE_CLASS);
    el.classList.remove(BUILDER_ELEMENT_HOVER_CLASS);
    el.classList.remove(BUILDER_SORTABLE_DRAG_CLASS);
    el.classList.remove(BUILDER_SORTABLE_GHOST_CLASS);
    el.classList.remove(BUILDER_SORTABLE_CHOSEN_CLASS);
    el.classList.remove('sortable-chosen');

    const actionsButtonGroup = el.querySelector(element ? `:scope > .${ACTIONS_BUTTON_GROUP}` : ` .${ACTIONS_BUTTON_GROUP}`);
    if (actionsButtonGroup) {
      actionsButtonGroup.remove();
    }
  });
};
